import React from "react";
import LimitErr from "Components/PreviewPopup/LimitErr";
import { URLS, makeApi } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import Utils from "Utils/utils";
import { CommonUtils } from "./commonUtils";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { importProgressJobs } from "../Dashboard/sequences/ProgressJobs/ImportProgressJobsStore";
import userDetail from "Stores/userDetail";
import { observable, action, computed } from "mobx";

// Detects whether a pasted LinkedIn URL is a post (vs a Sales Navigator search),
// so one import flow can intelligently branch on what the user pastes.
const isLinkedInPostUrl = (url = "") => {
  if (!url) return false;
  const u = url.toLowerCase();
  return (
    u.includes("/posts/") ||
    u.includes("/feed/update/") ||
    u.includes("urn:li:activity") ||
    u.includes("urn:li:share") ||
    u.includes("urn:li:ugcpost") ||
    /activity[-:]\d{6,}/.test(u)
  );
};

// Detects a LinkedIn event URL (e.g. linkedin.com/events/<slug-id>/), so we can
// branch to the event-attendees importer. Attendees are only importable when the
// connected account is attending the event.
const isLinkedInEventUrl = (url = "") => {
  if (!url) return false;
  const u = url.toLowerCase();
  return u.includes("/events/") || u.includes("eventattending=");
};

class PullContactsStore {
  @observable pasteInputValue = "";
  @observable showPopup = false;
  @observable searchLoading = false;
  @observable pullContactsCount = 0;
  @observable extractBtn = false;
  @observable extractBtnLoading = false;
  @observable showListSeq = false;
  @observable addToListChecked = false;
  @observable addToSeqChecked = false;
  @observable totalContactList = [];
  @observable listInfo = {};
  @observable disableExtractBtn = false;
  @observable sequenceList = [];
  @observable selectedSequence = {};
  @observable createListError = "";
  @observable allowSequence = true;
  @observable liJobStatus = {};
  // For a post URL, which engagement to import. Both can be selected.
  @observable includeReactions = true;
  @observable includeComments = false;
  @observable selectedLiAccountId = "";

  // Whether the pasted URL is a LinkedIn post (vs a Sales Navigator search).
  @computed get isPostUrl() {
    return isLinkedInEventUrl(this.pasteInputValue)
      ? false
      : isLinkedInPostUrl(this.pasteInputValue);
  }

  // Whether the pasted URL is a LinkedIn event (import the event's attendees).
  @computed get isEventUrl() {
    return isLinkedInEventUrl(this.pasteInputValue);
  }

  @computed get activeLiAccounts() {
    return userDetail?.activeLinkedInAccounts || [];
  }

  @computed get selectedLiAccount() {
    return (
      this.activeLiAccounts.find(acc => acc.id === this.selectedLiAccountId) ||
      null
    );
  }

  @computed get needsLiAccountSelection() {
    return this.activeLiAccounts.length > 1 && !this.selectedLiAccountId;
  }

  // A Sales Navigator saved-search import needs a connected Sales Navigator seat;
  // post and event imports work with any connected LinkedIn account. Checks the
  // account that will actually run the import: the one selected, or the sole
  // connected account when there's no ambiguity to pick from.
  @computed get hasSalesNavigatorAccount() {
    const acc =
      this.selectedLiAccount ||
      (this.activeLiAccounts.length === 1 ? this.activeLiAccounts[0] : null);
    return acc?.linkedinAccountType === "SALES_NAVIGATOR";
  }

  // A Recruiter search import needs an account with a Recruiter seat (recruiterEnabled).
  // Same "account that will actually run the import" rule as above.
  @computed get hasRecruiterAccount() {
    const acc =
      this.selectedLiAccount ||
      (this.activeLiAccounts.length === 1 ? this.activeLiAccounts[0] : null);
    return !!acc?.recruiterEnabled;
  }

  @action
  initLiAccountSelection = () => {
    if (!this.selectedLiAccountId && this.activeLiAccounts.length === 1) {
      this.selectedLiAccountId = this.activeLiAccounts[0].id;
    }
  };

  @action
  setSelectedLiAccountId = (id = "") => {
    this.selectedLiAccountId = id;
  };

  // A Sales Navigator company/account search — we import people, not companies, so this
  // is not a valid import URL (it used to fall through and be treated as a people search).
  @computed get isCompanySearchUrl() {
    const u = (this.pasteInputValue || "").toLowerCase();
    return (
      u.includes("/sales/search/company") ||
      u.includes("/sales/search/account") ||
      u.includes("/sales/lists/company")
    );
  }

  // A non-empty pasted URL we can't import contacts from: a Sales Navigator company
  // search, or any link that isn't a LinkedIn post, event, or (people) URL at all.
  @computed get isInvalidImportUrl() {
    const u = (this.pasteInputValue || "").trim().toLowerCase();
    if (!u) return false;
    if (this.isPostUrl || this.isEventUrl) return false;
    if (this.isCompanySearchUrl) return true;
    // Otherwise it must at least be a LinkedIn URL to be treated as a people search.
    return !u.includes("linkedin.com");
  }

  // A pasted LinkedIn Recruiter search URL (linkedin.com/talent/...), including a
  // project or saved search. Needs a Recruiter seat.
  @computed get isRecruiterSearchUrl() {
    return (this.pasteInputValue || "")
      .toLowerCase()
      .includes("linkedin.com/talent/");
  }

  // A pasted URL that is a Sales Navigator people search (needs an SN seat). Everything
  // that isn't a post, an event, a Recruiter search, or an invalid URL falls here.
  @computed get isSalesNavSearchUrl() {
    return (
      !!(this.pasteInputValue || "").trim() &&
      !this.isPostUrl &&
      !this.isEventUrl &&
      !this.isRecruiterSearchUrl &&
      !this.isInvalidImportUrl
    );
  }

  // Show the inline "needs Sales Navigator" note: an SN-search URL with no SN seat.
  @computed get needsSalesNavAccount() {
    return this.isSalesNavSearchUrl && !this.hasSalesNavigatorAccount;
  }

  // Show the inline "needs Recruiter" note: a Recruiter-search URL with no Recruiter seat.
  @computed get needsRecruiterAccount() {
    return (
      this.isRecruiterSearchUrl &&
      !this.isPostUrl &&
      !this.isEventUrl &&
      !this.hasRecruiterAccount
    );
  }

  // Backend engagementType param: "both" | "reactions" | "comments".
  @computed get engagementTypeParam() {
    if (this.includeReactions && this.includeComments) return "both";
    if (this.includeComments) return "comments";
    return "reactions";
  }

  @action
  setIncludeReactions = (value = true) => {
    this.includeReactions = value;
  };

  @action
  setIncludeComments = (value = false) => {
    this.includeComments = value;
  };

  @action
  setLiJobStatus = (value = {}) => {
    this.liJobStatus = value;
  };
  @action
  setAllowSequence = (val = "") => {
    this.allowSequence = val;
  };

  @action
  setPasteInputValue = (value = "") => {
    this.pasteInputValue = value;
  };

  @action
  setShowPopup = (value = false) => {
    this.showPopup = value;
  };

  @action
  setSearchLoading = (value = false) => {
    this.searchLoading = value;
  };

  @action
  setPullContactsCount = (value = false) => {
    this.pullContactsCount = value;
  };

  @action
  setExtractBtn = (value = false) => {
    this.extractBtn = value;
  };

  @action
  setExtractBtnLoading = (value = false) => {
    this.extractBtnLoading = value;
  };

  @action
  setShowListSeq = (value = false) => {
    this.showListSeq = value;
  };

  @action
  setAddToListChecked = (value = false) => {
    this.addToListChecked = value;
  };

  @action
  setAddToSeqChecked = (value = false) => {
    this.addToSeqChecked = value;
  };

  @action
  setTotalContactList = (val = []) => {
    this.totalContactList = val;
  };

  @action
  setListInfo = (value = {}) => {
    this.listInfo = value;
  };

  @action
  setDisableExtractBtn = (value = false) => {
    this.disableExtractBtn = value;
  };

  @action
  setSequenceList = (list = []) => {
    this.sequenceList = list;
  };

  @action
  setSelectedSequence = (seq = {}) => {
    this.selectedSequence = seq;
  };

  @action
  setCreateListError = (val = "") => {
    this.createListError = val;
  };

  @action
  resetFunc = () => {
    this.setSelectedSequence({});
    this.setListInfo({});
    this.setExtractBtn(false);
    this.setExtractBtnLoading(false);
    this.setDisableExtractBtn(false);
    this.setShowListSeq(false);
    this.setShowPopup(false);
    this.setAddToListChecked(false);
    this.setAddToSeqChecked(false);
    this.setPasteInputValue("");
    this.setCreateListError("");
    this.setIncludeReactions(true);
    this.setIncludeComments(false);
    this.setSelectedLiAccountId("");
    this.initLiAccountSelection();
  };

  @action
  getSequenceData = async (data = {}) => {
    if (data && Object.keys(data)?.length > 0) {
      const config = {
        url: URLS.listAllTriggerSequences,
        method: "POST",
        data
      };
      let response = await makeApi(config);
      if (response?.data) {
        this.setSequenceList([
          ...this.sequenceList,
          ...response?.data?.campaignSequenceResponseList
        ]);
        return response?.data;
      } else {
        Utils.showApiFailureResponse(response);
        return {};
      }
    }
  };

  @action
  getContactList = async () => {
    try {
      const config = {
        url: URLS.getContactList,
        method: "POST",
        data: {
          limit: 250,
          pageNo: 1
        }
      };
      let res = await makeApi(config);
      if (res?.data) {
        const { listResponse = {} } = res?.data || {};
        const { statusCode = "", list = [] } = listResponse || {};
        if (statusCode === 200) {
          this.setTotalContactList(list);
        }
      }
    } catch (err) {
      console.error("Error in fetching contact list", err);
    }
  };

  @action
  createContactList = async listName => {
    try {
      const config = {
        url: URLS.createContactList,
        method: "POST",
        data: { listName }
      };
      let res = await makeApi(config);
      if (res?.data?.statusCode === 200) {
        const { listResponse = {} } = res?.data || {};
        if (listResponse && Object.keys(listResponse)?.length > 0) {
          const { statusCode = "" } = listResponse || {};
          if (statusCode === 200) {
            this.setCreateListError("");
            this.getContactList();
            this.setDisableExtractBtn(false);
            this.setListInfo(listResponse?.list?.[0]);
          } else {
            if (statusCode === 409) {
              this.setCreateListError("List name already exists");
            } else {
              this.setCreateListError(
                "Create list failed. Please contact support"
              );
            }
            this.setDisableExtractBtn(true);
          }
        }
      } else {
        this.setDisableExtractBtn(true);
        this.setCreateListError("Create list failed. Please contact support");
      }
    } catch (err) {
      console.error("Error in creating contact list", err);
    }
  };

  @action
  lookupContacts = async history => {
    // Unsupported URL (e.g. a Sales Navigator company search) — the popup shows an inline
    // error and disables Search; guard here too so it never reaches the backend.
    if (this.isInvalidImportUrl) {
      this.setSearchLoading(false);
      return;
    }
    const isEvent = this.isEventUrl;
    const isPost = !isEvent && this.isPostUrl;
    // Only a Sales Navigator *search* URL needs a Sales Navigator seat; post/event
    // imports work with a regular connected account. The popup shows an inline note
    // (needsSalesNavAccount) and disables Search, so just guard here as a safety.
    if (
      !isEvent &&
      !isPost &&
      (this.needsSalesNavAccount || this.needsRecruiterAccount)
    ) {
      this.setSearchLoading(false);
      return;
    }
    if (this.needsLiAccountSelection) {
      this.setSearchLoading(false);
      return;
    }
    const config = {
      url: isEvent
        ? URLS.lookupEventAttendees
        : isPost
        ? URLS.lookupPostEngagement
        : URLS.lookupContacts,
      method: "POST",
      data: {
        url: this.pasteInputValue,
        liAccountId: this.selectedLiAccountId,
        ...(isPost ? { engagementType: this.engagementTypeParam } : {})
      }
    };
    let res = await makeApi(config);
    this.setSearchLoading(false);
    this.setExtractBtn(true);
    const { data = {} } = res || {};
    const responseCode = data?.responseCode;
    if (+responseCode === 200) {
      // For events the roster is only visible to an attending account; the backend
      // reports attending=false when the connected account has not joined the event.
      if (isEvent && data.attending === false) {
        this.setExtractBtn(false);
        toasterState.setToastMsg(
          "The connected LinkedIn account must be attending this event to import attendees.",
          "failure"
        );
        return;
      }
      this.setPullContactsCount(data.count || 0);
      if (data.count === 0) {
        this.setExtractBtn(false);
      }
    } else {
      this.setExtractBtn(false);
      if (+responseCode === 100) {
        toasterState.setToastMsg(
          "You’ve reached your daily search limit. Contact support.",
          "failure"
        );
        this.setExtractBtn(false);
      } else if (+responseCode === 403) {
        toasterState.setToastMsg(
          isEvent || isPost
            ? "LinkedIn account not connected"
            : this.isRecruiterSearchUrl
            ? "Recruiter account not connected"
            : "Sales navigator account not connected",
          "failure"
        );
      } else if (responseCode === 404) {
        toasterState.setToastMsg(
          "No contacts found for this URL. Please check and try again.",
          "failure"
        );
      } else if (+responseCode === 104) {
        this.setShowPopup(false);
        confirmationPopupState.setPopupValues({
          content: `The access to your LinkedIn account has expired. Please connect again.`,
          actionBtnText: "Connect",
          cancelBtnText: "Cancel",
          needCancelBtn: true,
          cancelCbk: () => confirmationPopupState.setShowPopup(false),
          callback: () => {
            history && history.push("/settings/account/linkedin");
            confirmationPopupState.setShowPopup(false);
          }
        });
        confirmationPopupState.setShowPopup(true);
      } else {
        toasterState.setToastMsg(
          "Something went wrong. Please try again later",
          "failure"
        );
      }
    }
  };

  @action
  getLiJobStatus = async (seqId = "") => {
    try {
      const config = {
        url: Utils.getReplacedUrl(URLS.linkedInImportsListSeq, seqId),
        method: "POST"
      };
      const res = await makeApi(config);
      const {
        totalActiveSearchJobs = 0,
        totalInProgressJobs = 0,
        jobs = []
      } = res?.data || {};
      if (res?.data) {
        this.liJobStatus = res?.data;
        importProgressJobs.setLiImportJobs(res?.data?.jobs);
        importProgressJobs.setTotalLiActiveJobs(+totalActiveSearchJobs);
        importProgressJobs.setTotalLiInProgressJobs(+totalInProgressJobs);
      } else {
        this.liJobStatus = {};
        importProgressJobs.setLiImportJobs([]);
        importProgressJobs.setTotalLiActiveJobs(0);
        importProgressJobs.setTotalLiInProgressJobs(0);
      }
    } catch (err) {
      this.liJobStatus = {};
      importProgressJobs.setLiImportJobs([]);
      importProgressJobs.setTotalLiActiveJobs(0);
      importProgressJobs.setTotalLiInProgressJobs(0);
    }
  };

  @action
  getAllLiJobStatus = async () => {
    try {
      const config = {
        url: URLS.getAllLiImportJobs,
        method: "GET"
      };
      const res = await makeApi(config);
      const {
        totalActiveSearchJobs = 0,
        totalInProgressJobs = 0,
        jobs = []
      } = res?.data || {};
      if (res?.data) {
        importProgressJobs.setTotalLiActiveJobs(+totalActiveSearchJobs);
        importProgressJobs.setTotalLiInProgressJobs(+totalInProgressJobs);
        this.liJobStatus = res?.data;
        importProgressJobs.setLiImportJobs(res?.data?.jobs);
      } else {
        this.liJobStatus = {};
        importProgressJobs.setLiImportJobs([]);
        importProgressJobs.setTotalLiActiveJobs(0);
        importProgressJobs.setTotalLiInProgressJobs(0);
      }
    } catch (err) {
      this.liJobStatus = {};
      importProgressJobs.setLiImportJobs([]);
      importProgressJobs.setTotalLiActiveJobs(0);
      importProgressJobs.setTotalLiInProgressJobs(0);
    }
  };

  @action
  extractContacts = async history => {
    const isEvent = this.isEventUrl;
    const isPost = !isEvent && this.isPostUrl;
    const sequencecId = this.addToSeqChecked ? this.selectedSequence.id : "";
    const config = {
      url: isEvent
        ? URLS.addEventAttendees
        : isPost
        ? URLS.addPostEngagement
        : URLS.extractContacts,
      method: "POST",
      data: {
        url: this.pasteInputValue,
        listId: this.listInfo.id ? [this.listInfo.id] : [],
        sequenceId: this.addToSeqChecked ? this.selectedSequence.id : "",
        totalContacts: this.pullContactsCount || 0,
        liAccountId: this.selectedLiAccountId,
        ...(isPost ? { engagementType: this.engagementTypeParam } : {})
      }
    };
    let res = await makeApi(config);
    const { data = {} } = res || {};
    this.resetFunc();
    const responseCode = data?.responseCode;

    if (+res?.data?.code === 202) {
      confirmationPopupState.setPopupValues({
        content: (
          <LimitErr
            headingContent={`Oops! You’re trying to configure limits beyond what’s available in your plan. Upgrade to a higher plan to increase your limit.`}
            limitMessage={`Sales Navigator Imports Limit reached.`}
          />
        ),
        dynamicDom: true,
        actionBtnText: "Okay",
        cancelBtnText: "Upgrade",
        cancelCbk: () => {
          confirmationPopupState.setShowPopup(false);
          history && history.push("/pricing");
        },
        callback: () => {
          confirmationPopupState.setShowPopup(false);
        }
      });
      confirmationPopupState.setShowPopup(true);
      return;
    }
    if (+responseCode === 200) {
      sequencecId && history.push(`${sequencecId}/#contacts`);
      confirmationPopupState.setPopupValues({
        content: `Your LinkedIn contacts extract is successfully scheduled. The contacts will be added in a few minutes. You can view the status on the Imports page.`,
        actionBtnText: "Go to Imports",
        callback: () => {
          history.push("/settings/imports/linkedin");
          confirmationPopupState.setShowPopup(false);
        }
      });
      confirmationPopupState.setShowPopup(true);
      sequencecId && this.getLiJobStatus(sequencecId);
      if (window.location.pathname === "/contacts") {
        this.getAllLiJobStatus();
      }
    } else if (+responseCode === 100) {
      toasterState.setToastMsg(
        "You’ve reached your daily search limit. Contact support.",
        "failure"
      );
    } else if (+responseCode === 403) {
      Utils.showAddLinkedInAccountPopup(
        history,
        "Connect your LinkedIn Sales Navigator account to continue. "
      );
    } else {
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "failure"
      );
    }
  };
}

const pullContactState = new PullContactsStore();

export { pullContactState };
